import { useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BrandButton } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";

const TEXT = {
  restore: { title: "Restore account", btn: "Restore account",
    desc: "The member account comes back with its details and links to registrations. No email is sent; the member signs in using “Forgot password?” to set a new password." },
  delete: { title: "Delete permanently", btn: "Delete permanently",
    desc: "This removed account is erased for good. Their unpaid registrations are moved to Deleted Registrations; paid registrations, payments and receipts are kept." },
};

const ActionDialog = ({ m, kind, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const t = TEXT[kind];
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/removed-members/${m.id}/${kind}`, { reason, confirm, removed_at: m.removed_at });
      toast.success(kind === "restore" ? `${m.name}'s account restored` : `${m.name} permanently deleted · ${r.data.registrations_deleted} unpaid registration(s) removed, ${r.data.registrations_kept} kept`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const ok = reason.trim().length >= 3 && (kind === "restore" || confirm.trim().toLowerCase() === m.email.toLowerCase());
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid={`removed-${kind}-dialog`}>
        <DialogHeader><DialogTitle className="font-heading">{t.title}: {m.name}</DialogTitle><DialogDescription>{t.desc}</DialogDescription></DialogHeader>
        <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" data-testid={`removed-${kind}-reason-input`} />
        {kind === "delete" && <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={`Type ${m.email} to confirm`} data-testid="removed-delete-confirm-input" />}
        <div className="flex justify-end gap-2">
          <BrandButton variant="outline" onClick={onClose} data-testid={`removed-${kind}-cancel`}>Cancel</BrandButton>
          <BrandButton variant={kind === "delete" ? "red" : "dark"} onClick={go} disabled={!ok || busy} data-testid={`removed-${kind}-confirm`}>{busy ? "Working…" : t.btn}</BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const RemovedActions = ({ m, onDone }) => {
  const [kind, setKind] = useState(null);
  return (
    <div className="flex gap-2">
      <BrandButton variant="outline" onClick={() => setKind("restore")} data-testid={`removed-restore-${m.id}`}><RotateCcw className="w-4 h-4" />Restore</BrandButton>
      <BrandButton variant="outline" onClick={() => setKind("delete")} className="!text-[#E60000]" data-testid={`removed-delete-${m.id}`}><Trash2 className="w-4 h-4" />Delete permanently</BrandButton>
      {kind && <ActionDialog m={m} kind={kind} onClose={() => setKind(null)} onDone={() => { setKind(null); onDone(); }} />}
    </div>
  );
};
